import React from 'react';
import { Lightbulb, Heart, FileText, Zap, Link, X, Loader2 } from 'lucide-react';

const typeConfig = {
  emotion: {
    icon: Heart,
    label: 'EMOTION',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-700',
    badgeBg: 'bg-amber-100',
    hoverBg: 'hover:bg-amber-100'
  },
  detail: {
    icon: FileText,
    label: 'DETAIL',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    badgeBg: 'bg-blue-100',
    hoverBg: 'hover:bg-blue-100'
  },
  tension: {
    icon: Zap,
    label: 'TENSION',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    badgeBg: 'bg-red-100',
    hoverBg: 'hover:bg-red-100'
  },
  connection: {
    icon: Link,
    label: 'CONNECTION',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    badgeBg: 'bg-green-100',
    hoverBg: 'hover:bg-green-100'
  }
};

const ExpansionSuggestions = ({
  suggestions,
  isAnalyzing,
  isExpanding,
  selectedSuggestion,
  onSuggestionClick,
  onCancel
}) => {
  if (isAnalyzing) {
    return (
      <div className="mt-6 p-6 bg-gray-50 border border-gray-200 rounded-xl">
        <div className="flex items-center justify-center space-x-3">
          <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
          <span className="text-gray-700 font-medium">Analyzing your story...</span>
        </div>
      </div>
    );
  }

  if (isExpanding) {
    return (
      <div className="mt-6 p-6 bg-primary-50 border border-primary-200 rounded-xl">
        <div className="flex items-center justify-center space-x-3">
          <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
          <span className="text-primary-700 font-medium">
            Expanding your story and regenerating email...
          </span>
        </div>
      </div>
    );
  }

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 animate-slide-up">
      <div className="bg-gradient-to-r from-gray-50 to-primary-50 border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-gray-900">Strengthen Your Story</h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Click a suggestion to expand your story and regenerate a better email:
        </p>

        <div className="space-y-3">
          {suggestions.map((suggestion) => {
            const config = typeConfig[suggestion.type] || typeConfig.detail;
            const Icon = config.icon;
            const isSelected = selectedSuggestion?.id === suggestion.id;

            return (
              <button
                key={suggestion.id}
                onClick={() => onSuggestionClick(suggestion)}
                disabled={isExpanding}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                  isSelected
                    ? `${config.bgColor} ${config.borderColor} ring-2 ring-offset-1 ring-${config.textColor.replace('text-', '')}`
                    : `bg-white border-gray-200 ${config.hoverBg} hover:border-gray-300`
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`p-1.5 rounded-md ${config.badgeBg}`}>
                    <Icon className={`w-4 h-4 ${config.textColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`text-xs font-bold ${config.textColor} ${config.badgeBg} px-2 py-0.5 rounded`}>
                        {config.label}
                      </span>
                    </div>
                    <p className="text-gray-900 font-medium text-sm leading-relaxed">
                      "{suggestion.prompt}"
                    </p>
                    {suggestion.hint && (
                      <p className="text-gray-500 text-xs mt-1.5 italic">
                        {suggestion.hint}
                      </p>
                    )}
                  </div>
                  <div className={`text-xs font-medium ${config.textColor} whitespace-nowrap`}>
                    Use this →
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
          >
            Keep Current Version
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpansionSuggestions;
